// @ts-nocheck
// Unified analytics + error monitoring façade.
//
// Wraps Plausible (page views, custom events) and Sentry (errors). Each is
// activated only if its env var is present, so dev/preview builds without
// keys silently no-op.
//
// Plausible: loaded as a script tag in public/index.html (see comment there).
//   Exposes window.plausible(eventName, { props }).
// Sentry:   dynamic-imported below to keep the main bundle small until
//   we know the user actually wants it. Replace the stub call with the
//   real init once REACT_APP_SENTRY_DSN is set in Vercel.

type EventProps = Record<string, string | number | boolean>;

export function trackEvent(name: string, props?: EventProps): void {
  // Plausible — safe to call before script loads; queue is created on the fly.
  const w = window as any;
  if (typeof w.plausible === "function") {
    w.plausible(name, props ? { props } : undefined);
  }
}

export function captureError(err: unknown, context?: EventProps): void {
  const w = window as any;
  if (w.Sentry && typeof w.Sentry.captureException === "function") {
    w.Sentry.captureException(err, context ? { extra: context } : undefined);
  } else {
    // Dev fallback — still surface to console.
    // eslint-disable-next-line no-console
    console.error("[captureError]", err, context);
  }
}

export async function initMonitoring(): Promise<void> {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;

  // Dynamic import so Sentry is only fetched when actually configured.
  // Requires `@sentry/react` in package.json — install before flipping on.
  try {
    const Sentry = await import(/* webpackChunkName: "sentry" */ "@sentry/react");
    Sentry.init({
      dsn,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      environment: process.env.NODE_ENV,
    });
    (window as any).Sentry = Sentry;
  } catch {
    // @sentry/react not installed yet — silent no-op. Once installed,
    // captureError will start reporting automatically.
  }
}
